// ./src/components/generic/ScheduleTypeSelector/ScheduleTypeSelector.tsx
//
// Chip-based single-select for schedule type (Weekly / Monthly / Yearly / One-Time).
// Uses individually controlled Mantine Chips (not Chip.Group) to support
// deselection when the field is not required. Chip.Group with multiple=false
// renders radio inputs which cannot be deselected natively.
//
// Supports optional one-time option via showOneTime prop.
// Styling matches TravelTypeSelector pattern.

import React, { useMemo, useCallback, useEffect } from "react";
import { Chip, Input, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { VEHICLE_TASK_SCHEDULE_TYPE_OPTIONS, VehicleTaskOption } from "@/config/vehicleTaskOptions";

import { SCHEDULE_TYPE } from "@/utils/api/ExpenseScheduleService";
import type { ScheduleTypeValue } from "@/utils/api/ExpenseScheduleService";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface ScheduleTypeSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected schedule type value */
  value?: ScheduleTypeValue | string | null | undefined;

  /** Callback fired when selection changes: (name, value) */
  onChange?: (name: string, value: ScheduleTypeValue | null) => void;

  /**
   * Whether the field is required.
   * When true: auto-selects first option if value is null, user cannot deselect.
   * When false: clicking the selected chip deselects it.
   */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the input wrapper (for label/error consistency) */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Optional label for the field */
  label?: string;

  /** Chip variant: outline (default) or filled */
  variant?: "outline" | "filled" | "light";

  /** Whether to show the One-Time option (default: true) */
  showOneTime?: boolean;
}

// ============================================
// Styles
// ============================================

const CHIP_LABEL_STYLES = {
  borderRadius: "var(--mantine-radius-md)",
  height: "42px",
  paddingLeft: "12px",
  paddingRight: "14px",
  fontSize: "var(--mantine-font-size-sm)",
  width: "100%",
  justifyContent: "center",
};

const CHIP_ICON_WRAPPER_STYLES = {
  width: "auto",
  height: "20px",
  paddingRight: "4px",
};

const CHIP_ROOT_STYLES = {
  flex: "1 1 0",
  minWidth: 0,
};

const GROUP_STYLES: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--mantine-spacing-xs)",
};

// ============================================
// Component
// ============================================

const ScheduleTypeSelectorComponent: React.FC<ScheduleTypeSelectorProps> = ({
  name = "scheduleType",
  value,
  onChange,
  required = false,
  error,
  disabled,
  size = "md",
  label,
  variant = "outline",
  showOneTime = true,
}) => {
  const { t } = useTranslation(["vehicleTasks"]);

  // Filter options based on showOneTime
  const filteredOptions = useMemo(
    () =>
      showOneTime
        ? VEHICLE_TASK_SCHEDULE_TYPE_OPTIONS
        : VEHICLE_TASK_SCHEDULE_TYPE_OPTIONS.filter(
          (opt) => opt.value !== SCHEDULE_TYPE.ONE_TIME
        ),
    [showOneTime]
  );

  // Auto-select first option when required and value is null/undefined
  useEffect(() => {
    if (required && value == null && onChange && filteredOptions.length > 0) {
      onChange(name, filteredOptions[0].value as ScheduleTypeValue);
    }
  }, [required, value, name, onChange, filteredOptions]);

  // Clear value if current selection is ONE_TIME and showOneTime becomes false
  useEffect(() => {
    if (!showOneTime && value === SCHEDULE_TYPE.ONE_TIME && onChange) {
      onChange(name, null);
    }
  }, [showOneTime, value, name, onChange]);

  // Handle individual chip toggle with single-select semantics.
  // Each Chip is a controlled checkbox, so onChange fires with
  // checked=true (selecting) or checked=false (deselecting).
  const handleChipChange = useCallback(
    (chipValue: string, checked: boolean) => {
      if (checked) {
        onChange?.(name, chipValue as ScheduleTypeValue);
      } else {
        // Unchecking: only allowed when not required
        if (!required) {
          onChange?.(name, null);
        }
      }
    },
    [name, required, onChange]
  );

  // Build individually controlled chip elements (no Chip.Group)
  const chips = useMemo(() => {
    return filteredOptions.map((config: VehicleTaskOption) => {
      const chipValue = String(config.value);
      const isChecked = chipValue === value;
      const translatedLabel = t(`vehicleTasks:${config.labelKey}`, {
        defaultValue: chipValue,
      });

      return (
        <Chip
          key={chipValue}
          checked={isChecked}
          onChange={(checked) => handleChipChange(chipValue, checked)}
          color={config.color}
          variant={variant}
          size="sm"
          disabled={disabled}
          icon={
            config.icon ? (
              <AppIcon iconName={config.icon} size={20} />
            ) : undefined
          }
          styles={{
            label: CHIP_LABEL_STYLES,
            iconWrapper: CHIP_ICON_WRAPPER_STYLES,
            root: CHIP_ROOT_STYLES,
          }}
        >
          {translatedLabel}
        </Chip>
      );
    });
  }, [filteredOptions, value, t, variant, disabled, handleChipChange]);

  return (
    <Input.Wrapper
      label={label}
      required={required}
      error={error}
      size={size}
    >
      <Box mt={label ? 4 : 0} style={GROUP_STYLES}>
        {chips}
      </Box>
    </Input.Wrapper>
  );
};

export const ScheduleTypeSelector = React.memo(ScheduleTypeSelectorComponent);